/**
 * What a supplier offered, read straight out of the workbook they returned — before anyone turns
 * it into a `Supplier Quotation`.
 *
 * ---- Why this is here ----
 *
 * Measured live 2026-08-08 on RFQ-2026-00011. Shiby's reply body contained nothing of theirs, only
 * our own mail quoted back; the prices were in the attached workbook (`UNIT PRICE 23`, `AMOUNT
 * 690`, `AED`, `UOM BAG`). The purchaser's only way to see any of that was to press "Read as
 * quotation", which CREATES a document. So the choice on offer was "commit, then look".
 *
 * This reads and shows. It writes nothing, and it deliberately does not make the reply official —
 * `parse_supplier_reply` cannot yet tell a quotation from a refusal (a "sorry, no stock" mail came
 * back as a quotation at rate 0.00 reporting success), so a human still presses the button. What
 * changes is that they now press it knowing what is in the file.
 *
 * ---- Why the parser is loaded lazily ----
 *
 * `xlsxPreview` pulls in `read-excel-file`, which has no business sitting in the main chunk for a
 * panel most rows never expand. Same dynamic-import rule the mail attachment preview follows.
 */

import { useEffect, useState } from 'react'
import { Badge } from '../../components/Badge'
import { Money } from '../../components/Money'
import { useMailMessage } from '../../lib/api/useMail'
import { pickRfqSheet, readWorkbookOffer, type WorkbookOffer } from '../../lib/rfqReplyWorkbook'
import type { Currency } from '../../lib/types'

/* Each member carries a LITERAL `status`. Writing the first two as one `'idle' | 'loading'` member
   leaves TypeScript unable to discriminate the union, so `offerState.offer` stays unresolved after
   every other case has been returned. */
type OfferState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'unreadable'; detail: string; transient?: boolean }
  | { status: 'ready'; offer: WorkbookOffer }

/**
 * The workbook is fetched and parsed in an effect rather than a query because the parser itself is
 * a dynamic import — the unit of work is "download, then lazily load a parser, then parse", which
 * is not a plain fetch and has nothing worth sharing in the query cache beyond the blob, which
 * `attachmentBlob` already caches underneath.
 */
function useWorkbookOffer(fileUrl: string | null): OfferState {
  const [state, setState] = useState<OfferState>({ status: 'idle' })

  useEffect(() => {
    if (fileUrl === null) {
      setState({ status: 'none' })
      return
    }
    let cancelled = false
    setState({ status: 'loading' })
    void (async () => {
      try {
        const { fetchAndParseXlsxPreview } = await import('../../lib/xlsxPreview')
        // A download that fails is retried twice before it counts (2026-09-19): one dropped
        // fetch used to read as "no workbook", and the server's parser was offered in its place.
        let result = await fetchAndParseXlsxPreview(fileUrl)
        for (let attempt = 0; attempt < 2 && !result.ok && result.reason === 'fetch_failed' && !cancelled; attempt += 1) {
          await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)))
          result = await fetchAndParseXlsxPreview(fileUrl)
        }
        if (cancelled) return
        if (!result.ok) {
          setState({ status: 'unreadable', detail: result.detail, transient: result.reason === 'fetch_failed' })
          return
        }
        const sheet = pickRfqSheet(result.preview.sheets)
        const offer = sheet ? readWorkbookOffer(sheet) : null
        // `null` here means the attachment is a spreadsheet but not our template — a catalogue, a
        // scan, the supplier's own format. Saying so is honest; guessing at its columns is not.
        setState(offer ? { status: 'ready', offer } : { status: 'unreadable', detail: 'not the RFQ template' })
      } catch (error) {
        if (!cancelled) setState({ status: 'unreadable', detail: (error as Error).message })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [fileUrl])

  return state
}

const cellStyle: React.CSSProperties = { padding: '4px 8px', fontSize: 12 }

/**
 * The offer's headline numbers, for a COLLAPSED table row — how many lines were priced and what
 * they come to.
 *
 * A row summary has to be worth reading on its own: "12 lines · AED 4,180" is what tells a
 * purchaser whether opening this one is worth it. Returns `null` while unknown, so a row never
 * claims a total it does not have.
 */
export function useParsedOffer(replyId: string | null, rfqId: string): WorkbookOffer | null {
  return useParsedOfferState(replyId, rfqId).offer
}

/**
 * The offer AND whether it is still being read (2026-09-19). `SupplierReplyPanel` used to see
 * `null` for both "no workbook" and "workbook still downloading", and offered "Read as quotation"
 * — the SERVER's parser — during the download. ENQ-2026-00238: the returned workbook said UNIT
 * PRICE 56, the button was pressed while it was loading, `parse_supplier_reply` filed SQ-2026-00387
 * at rate 0.00, and the comparison showed a free offer. While `reading`, nothing may be filed.
 */
export function useParsedOfferState(replyId: string | null, rfqId: string): { offer: WorkbookOffer | null; reading: boolean; downloadFailed: boolean } {
  const message = useMailMessage(replyId)
  const attachment = (message.data?.attachments ?? []).find((a) => a.kind === 'xlsx') ?? null
  const state = useWorkbookOffer(attachment?.preview_url ?? null)
  const reading = message.isPending || state.status === 'idle' || state.status === 'loading'
  // The workbook is there but could not be downloaded even after retries: not a case for the
  // server's parser (it would read nothing and file 0.00) — for a reload.
  const downloadFailed = state.status === 'unreadable' && state.transient === true
  if (state.status !== 'ready') return { offer: null, reading, downloadFailed }
  // Belongs to a DIFFERENT RFQ — the caller must not file these prices here. `ReplyOffer` shows the
  // warning; this returns nothing so nothing can be created from it.
  if (state.offer.rfq_id !== null && state.offer.rfq_id !== rfqId) return { offer: null, reading: false, downloadFailed: false }
  return { offer: state.offer, reading: false, downloadFailed: false }
}

export function useOfferSummary(replyId: string | null, rfqId: string): {
  lines: number
  priced: number
  total: number
  currency: string
} | null {
  const message = useMailMessage(replyId)
  const attachment = (message.data?.attachments ?? []).find((a) => a.kind === 'xlsx') ?? null
  const state = useWorkbookOffer(attachment?.preview_url ?? null)

  if (state.status !== 'ready') return null
  const { offer } = state
  if (offer.rfq_id !== null && offer.rfq_id !== rfqId) return null

  return {
    lines: offer.lines.length,
    priced: offer.priced_count,
    // Prefer the supplier's own AMOUNT column; fall back to qty × unit price only where they left
    // it blank, rather than recomputing over a figure they stated themselves.
    total: offer.lines.reduce(
      (sum, l) => sum + (l.amount ?? (l.unit_price !== null && l.qty !== null ? l.unit_price * l.qty : 0)),
      0,
    ),
    currency: offer.currency ?? '',
  }
}

export function ReplyOffer({ replyId, rfqId }: { replyId: string; rfqId: string }) {
  // The reply's attachments come from `mail.get_email`, which this hook already wraps — no second
  // way of reading a Communication, and it shares the mail screen's cache entry.
  const message = useMailMessage(replyId)
  const attachment = (message.data?.attachments ?? []).find((a) => a.kind === 'xlsx') ?? null
  const offerState = useWorkbookOffer(attachment?.preview_url ?? null)

  const loading = <div style={{ fontSize: 12, color: 'var(--muted)', padding: '4px 0' }}>Reading the attached workbook…</div>
  if (message.isPending) return loading
  // Checked on its own rather than `||`'d with the above, so the union actually narrows.
  if (offerState.status === 'idle' || offerState.status === 'loading') return loading

  // No spreadsheet came back. Not an error — plenty of suppliers answer in prose.
  if (offerState.status === 'none') return null

  if (offerState.status === 'unreadable') {
    return (
      <div style={{ fontSize: 12, color: 'var(--warn)', padding: '4px 0' }}>
        Couldn't read the attached workbook ({offerState.detail}) — open it from the mail, or read the reply as a
        quotation.
      </div>
    )
  }

  const { offer } = offerState

  /*
    A supplier who replies attaching the WRONG workbook must not have those prices shown under this
    RFQ. The file states which RFQ it belongs to, so this is checkable rather than assumed — and a
    mismatch is worth saying out loud, because it is exactly the kind of thing that otherwise gets
    filed against the wrong supplier.
  */
  if (offer.rfq_id !== null && offer.rfq_id !== rfqId) {
    return (
      <div style={{ fontSize: 12, color: 'var(--danger)', padding: '4px 0' }}>
        ⚠ The attached workbook is for <b>{offer.rfq_id}</b>, not {rfqId} — not showing its prices here.
      </div>
    )
  }

  if (offer.priced_count === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--muted)', padding: '4px 0' }}>
        They returned the workbook with no prices filled in.
      </div>
    )
  }

  // The workbook's own currency, never the RFQ's: what the supplier typed is what they meant to
  // quote in, and assuming otherwise silently restates their price in another currency.
  const currency = (offer.currency ?? '') as Currency
  const anyRemark = offer.lines.some((l) => l.remark !== '' || l.delivery !== '')

  return (
    <div style={{ padding: '4px 0 2px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <Badge tone="ok">
          {offer.priced_count} line{offer.priced_count === 1 ? '' : 's'} priced
        </Badge>
        <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>
          from the attached workbook · not yet a quotation
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: 'var(--muted)', fontSize: 11, textAlign: 'left' }}>
            <th style={cellStyle}>Item</th>
            <th style={cellStyle}>Qty · UOM</th>
            <th style={{ ...cellStyle, textAlign: 'right' }}>Unit price</th>
            <th style={{ ...cellStyle, textAlign: 'right' }}>Amount</th>
            {anyRemark ? <th style={cellStyle}>Delivery · remarks</th> : null}
          </tr>
        </thead>
        <tbody>
          {offer.lines.map((line, index) => (
            <tr key={`${line.opportunity_item || line.item_code}-${index}`} style={{ borderTop: '1px solid var(--line)' }}>
              <td style={cellStyle}>
                <div style={{ fontWeight: 600 }}>{line.description || line.item_code || '—'}</div>
                {line.description && line.item_code ? (
                  <div className="mono" style={{ color: 'var(--muted)', fontSize: 11 }}>
                    {line.item_code}
                  </div>
                ) : null}
              </td>
              <td style={cellStyle}>
                {line.qty ?? '—'} {line.uom}
              </td>
              <td style={{ ...cellStyle, textAlign: 'right' }}>
                {/* A blank price is shown as a dash, never as zero — a nil rate is what makes a
                    decline look like an offer of free goods. */}
                {line.unit_price === null ? (
                  <span style={{ color: 'var(--muted)' }}>not priced</span>
                ) : currency === ('' as Currency) ? (
                  <b>{line.unit_price}</b>
                ) : (
                  <Money value={{ amount: line.unit_price, currency }} />
                )}
              </td>
              <td style={{ ...cellStyle, textAlign: 'right' }}>
                {line.amount === null ? (
                  <span style={{ color: 'var(--muted)' }}>—</span>
                ) : currency === ('' as Currency) ? (
                  line.amount
                ) : (
                  <Money value={{ amount: line.amount, currency }} />
                )}
              </td>
              {anyRemark ? (
                <td style={{ ...cellStyle, color: 'var(--muted)' }}>
                  {[line.delivery, line.remark].filter((s) => s !== '').join(' · ') || '—'}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {offer.currency === null ? (
        <div style={{ fontSize: 11.5, color: 'var(--warn)', padding: '4px 8px' }}>
          The workbook names no currency — these figures are unpriced numbers until one is confirmed.
        </div>
      ) : null}
    </div>
  )
}
